import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, FolderOpen, FileText, Calendar, AlertTriangle, ExternalLink, ShieldCheck, FileBadge2, FileWarning, BookOpenCheck, CreditCard, Plane, IdCard } from 'lucide-react';
import { toast } from 'sonner';
import FileUpload from '../components/FileUpload';
import EditEntityDialog from '../components/EditEntityDialog';
import DeleteButton from '../components/DeleteButton';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'CS_SUPERVISOR'];
const DELETE_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'];

const FOLDERS = [
  { key: 'license',         label: 'Company Licenses',  icon: FileBadge2,    color: 'amber',   desc: 'Trade licenses, civil defence, ADDC/DEWA registrations' },
  { key: 'warranty',        label: 'Warranties',        icon: BookOpenCheck, color: 'sky',     desc: 'Equipment + contractor warranty docs' },
  { key: 'insurance',       label: 'Insurance',         icon: ShieldCheck,   color: 'emerald', desc: 'Property, liability, workmen, vehicle' },
  { key: 'staff_passport',  label: 'Staff Passports',   icon: CreditCard,    color: 'violet',  desc: 'Employee passport copies' },
  { key: 'staff_visa',      label: 'Staff Visas',       icon: Plane,         color: 'fuchsia', desc: 'Residency / work visa copies' },
  { key: 'staff_id',        label: 'Staff IDs',         icon: IdCard,        color: 'teal',    desc: 'Emirates ID / national ID copies' },
  { key: 'other',           label: 'Other',             icon: FolderOpen,    color: 'slate',   desc: 'Anything else' },
];

const COLOR_MAP = {
  amber:   { bg: 'bg-amber-100',   text: 'text-amber-700',   border: 'border-amber-300',   ring: 'ring-amber-500' },
  sky:     { bg: 'bg-sky-100',     text: 'text-sky-700',     border: 'border-sky-300',     ring: 'ring-sky-500' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300', ring: 'ring-emerald-500' },
  violet:  { bg: 'bg-violet-100',  text: 'text-violet-700',  border: 'border-violet-300',  ring: 'ring-violet-500' },
  fuchsia: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', border: 'border-fuchsia-300', ring: 'ring-fuchsia-500' },
  teal:    { bg: 'bg-teal-100',    text: 'text-teal-700',    border: 'border-teal-300',    ring: 'ring-teal-500' },
  slate:   { bg: 'bg-slate-100',   text: 'text-slate-700',   border: 'border-slate-300',   ring: 'ring-slate-500' },
};

const STATUS_BADGE = {
  expired:        'bg-rose-100 text-rose-800 border border-rose-200',
  expiring_soon:  'bg-amber-100 text-amber-800 border border-amber-200',
  valid:          'bg-emerald-100 text-emerald-800 border border-emerald-200',
  no_expiry:      'bg-slate-100 text-slate-700 border border-slate-200',
};

const EMPTY = { title: '', category: 'license', issued_by: '', issue_date: '', expiry_date: '', document_number: '', subject_user_id: '', notes: '', file_url: '', file_name: '' };

export default function DocumentVault() {
  const { user } = useContext(AuthContext);
  const [docs, setDocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({ by_category: {}, total: 0, expired: 0, expiring_soon: 0 });
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState('license');
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        axios.get(`${API}/vault/documents`, { withCredentials: true }),
        axios.get(`${API}/vault/summary`, { withCredentials: true }),
      ]);
      setDocs(d.data || []);
      setSummary(s.data || { by_category: {}, total: 0, expired: 0, expiring_soon: 0 });
    } catch {
      toast.error('Failed to load documents');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    if (MANAGE.includes(user?.role)) {
      axios.get(`${API}/users`, { withCredentials: true }).then(r => setUsers(r.data || [])).catch(() => {});
    }
  }, [user]);

  const filtered = docs.filter(d => d.category === activeFolder);
  const isStaffFolder = activeFolder.startsWith('staff_');

  const handleCreate = async () => {
    if (!draft.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    if (draft.category.startsWith('staff_') && !draft.subject_user_id) {
      toast.error('Select the staff member for this document');
      return;
    }
    try {
      const payload = { ...draft };
      if (!payload.subject_user_id) delete payload.subject_user_id;
      await axios.post(`${API}/vault/documents`, payload, { withCredentials: true });
      toast.success('Document added');
      setShowNew(false);
      setDraft({ ...EMPTY, category: activeFolder });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const userLabel = (id) => users.find(u => u.id === id)?.name || id || '—';

  const folderMeta = FOLDERS.find(f => f.key === activeFolder) || FOLDERS[0];
  const FolderIcon = folderMeta.icon;
  const colors = COLOR_MAP[folderMeta.color];

  return (
    <Layout>
      <div data-testid="vault-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <FolderOpen className="w-6 h-6 text-amber-600" />Document Vault
            </h1>
            <p className="text-sm text-muted-foreground">Centralised store for licenses, warranties, insurance, and staff documents — with expiry tracking.</p>
          </div>
          {MANAGE.includes(user?.role) && (
            <Dialog open={showNew} onOpenChange={(v) => { setShowNew(v); if (v) setDraft({ ...EMPTY, category: activeFolder }); }}>
              <DialogTrigger asChild>
                <Button data-testid="new-vault-doc-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                  <Plus className="w-4 h-4 mr-1" />Add Document
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Add Document</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2 max-h-[65vh] overflow-y-auto pr-1">
                  <div>
                    <Label>Folder</Label>
                    <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                      <SelectTrigger data-testid="vault-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FOLDERS.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Title *</Label><Input data-testid="vault-title-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. Trade License 2026" /></div>
                  {draft.category.startsWith('staff_') && (
                    <div>
                      <Label>Staff member *</Label>
                      <Select value={draft.subject_user_id} onValueChange={(v) => setDraft({ ...draft, subject_user_id: v })}>
                        <SelectTrigger data-testid="vault-subject-select"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                        <SelectContent>
                          {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} — {u.role}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Document Number</Label><Input value={draft.document_number} onChange={(e) => setDraft({ ...draft, document_number: e.target.value })} /></div>
                    <div><Label>Issued By</Label><Input value={draft.issued_by} onChange={(e) => setDraft({ ...draft, issued_by: e.target.value })} placeholder="Authority / supplier" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Issue Date</Label><Input type="date" value={draft.issue_date} onChange={(e) => setDraft({ ...draft, issue_date: e.target.value })} /></div>
                    <div><Label>Expiry Date</Label><Input data-testid="vault-expiry-input" type="date" value={draft.expiry_date} onChange={(e) => setDraft({ ...draft, expiry_date: e.target.value })} /></div>
                  </div>
                  <div>
                    <Label>Attachment</Label>
                    <FileUpload
                      value={draft.file_url ? [draft.file_url] : []}
                      onChange={(urls) => setDraft({ ...draft, file_url: urls[0] || '' })}
                      maxFiles={1}
                    />
                  </div>
                  <div><Label>Notes</Label><Textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></div>
                  <Button data-testid="submit-vault-doc-btn" onClick={handleCreate} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">Save document</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4"><div className="text-xs uppercase tracking-wide text-slate-500">Total</div><div data-testid="vault-total" className="text-2xl font-bold text-foreground">{summary.total}</div></Card>
          <Card className="p-4"><div className="text-xs uppercase tracking-wide text-rose-600">Expired</div><div data-testid="vault-expired" className="text-2xl font-bold text-rose-600">{summary.expired}</div></Card>
          <Card className="p-4"><div className="text-xs uppercase tracking-wide text-amber-600">Expiring soon</div><div data-testid="vault-expiring" className="text-2xl font-bold text-amber-600">{summary.expiring_soon}</div></Card>
          <Card className="p-4"><div className="text-xs uppercase tracking-wide text-slate-500">Folders</div><div className="text-2xl font-bold text-foreground">{FOLDERS.length}</div></Card>
        </div>

        {/* Folder strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {FOLDERS.map(f => {
            const Icon = f.icon;
            const c = COLOR_MAP[f.color];
            const active = activeFolder === f.key;
            const count = summary.by_category?.[f.key] || 0;
            return (
              <button
                key={f.key}
                type="button"
                data-testid={`folder-${f.key}`}
                onClick={() => setActiveFolder(f.key)}
                className={`p-3 rounded-xl border-2 text-left transition ${
                  active ? `${c.bg} ${c.border} ring-2 ${c.ring}` : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className={`flex items-center gap-2 ${c.text}`}>
                  <Icon className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase tracking-wide">{count}</span>
                </div>
                <div className="text-sm font-semibold text-foreground mt-1">{f.label}</div>
              </button>
            );
          })}
        </div>

        {/* Active folder list */}
        <Card className={`p-5 border-l-4 ${colors.border}`}>
          <div className="flex items-center gap-2 mb-4">
            <div className={`p-2 rounded-lg ${colors.bg} ${colors.text}`}><FolderIcon className="w-5 h-5" /></div>
            <div>
              <h2 className="font-semibold text-foreground">{folderMeta.label}</h2>
              <p className="text-xs text-muted-foreground">{folderMeta.desc}</p>
            </div>
          </div>
          {loading ? <div className="py-12 text-center text-muted-foreground">Loading…</div> : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">No documents in this folder yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
                    <th className="py-2 pr-3">Title</th>
                    {isStaffFolder && <th className="py-2 pr-3">Staff</th>}
                    <th className="py-2 pr-3">Doc #</th>
                    <th className="py-2 pr-3">Issued</th>
                    <th className="py-2 pr-3">Expiry</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">File</th>
                    {MANAGE.includes(user?.role) && <th className="py-2 pr-3">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(d => (
                    <tr key={d.id} data-testid={`vault-row-${d.id}`} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2 pr-3 font-medium text-slate-900">{d.title}</td>
                      {isStaffFolder && <td className="py-2 pr-3 text-slate-700">{userLabel(d.subject_user_id)}</td>}
                      <td className="py-2 pr-3 text-slate-700">{d.document_number || '—'}</td>
                      <td className="py-2 pr-3 text-slate-700">{d.issue_date || '—'}</td>
                      <td className="py-2 pr-3 text-slate-700">{d.expiry_date || '—'}</td>
                      <td className="py-2 pr-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[d.expiry_status]}`}>
                          {d.expiry_status === 'expired' && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                          {(d.expiry_status || 'no_expiry').replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        {d.file_url ? <a href={d.file_url} target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" />Open</a> : '—'}
                      </td>
                      {MANAGE.includes(user?.role) && (
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-1">
                            <EditEntityDialog
                              item={d}
                              endpoint="vault/documents"
                              testIdPrefix="vault"
                              title="Edit Document"
                              onSaved={fetchAll}
                              fields={[
                                { key: 'title', label: 'Title' },
                                { key: 'category', label: 'Folder', type: 'select', options: FOLDERS.map(f => ({ value: f.key, label: f.label })) },
                                { key: 'document_number', label: 'Document Number' },
                                { key: 'issued_by', label: 'Issued By' },
                                { key: 'issue_date', label: 'Issue Date', type: 'text', placeholder: 'YYYY-MM-DD' },
                                { key: 'expiry_date', label: 'Expiry Date', type: 'text', placeholder: 'YYYY-MM-DD' },
                                { key: 'file_url', label: 'File URL' },
                                { key: 'notes', label: 'Notes', type: 'textarea', rows: 2 },
                              ]}
                            />
                            {DELETE_ROLES.includes(user?.role) && (
                              <DeleteButton endpoint="vault/documents" id={d.id} label={`document "${d.title}"`} testId={`delete-vault-${d.id}`} onDeleted={fetchAll} />
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
